import { Product } from "@omni/types/api/product/bff/product.model";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { CloudStore } from "@omni/types/api/store/cloud/store.model";
import { isDefined } from "@omni/utils/helper.utils";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";

import {
    BundleProductModel,
    ProductBundleModel
} from "@/api/mappers/productBundles.mapper";
import { ProductBundleComponent } from "@/components/Product/ProductBundles/components/ProductBundle/productBundle.component";
import { StorePickerModal } from "@/components/Store/StorePicker/storePickerModal.container";
import { StorePickerFeature } from "@/components/Store/StorePicker/types/storePickerFeature.enum";
import { StorePickerType } from "@/components/Store/StorePicker/types/storePickerType.enum";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import { getCart } from "@/redux/modules/api/data/cart/cart.selectors";
import { getDeliveryType } from "@/redux/modules/value/productDetail/productDetail.module";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { GetStoreStrategy } from "@/services/store.service";
import { getPossibleDeliveryOptions } from "@/utils/deliveryMethod";
import { StorePickerModalStates } from "@/utils/enums";
import { useStoreCode } from "@/utils/hooks/store/useStoreCode.hook";

type ProductBundleProps = {
    mainProduct: Product<ProductFieldSet.ProductTile>;
    productBundle: ProductBundleModel;
};

type AddToCartEntry = {
    product: Product<ProductFieldSet.ProductTile>;
    quantity: number;
    shopId?: string;
    deliveryType: DeliveryType;
};

const isDSVOnly = (product: Product<ProductFieldSet.ProductTile>) =>
    product.deliveryTypes.length === 1 &&
    product.deliveryTypes[0] === DeliveryType.HomeDelivery;

const getDefaultAddToCartOptions = (
    product: Product<ProductFieldSet.ProductTile>
) => ({
    deliveryType: DeliveryType.HomeDelivery,
    quantity: product.minimumOrderQuantity ?? 1
});

const getDeliveryOptions = (
    product: Product<ProductFieldSet.ProductTile>,
    preferredStoreCode: string | undefined
) => {
    if (isDSVOnly(product)) {
        return getDefaultAddToCartOptions(product);
    } else {
        const deliveryOptions = getPossibleDeliveryOptions(
            product.productVisibilityType,
            product.deliveryTypes || []
        );
        const preferredDeliveryType = getDeliveryType();

        const deliveryType = deliveryOptions.includes(preferredDeliveryType)
            ? preferredDeliveryType
            : deliveryOptions[0];

        return deliveryType === DeliveryType.ClickAndCollect
            ? {
                  deliveryType: deliveryType,
                  quantity: 1,
                  shopId: preferredStoreCode
              }
            : {
                  deliveryType: deliveryType,
                  quantity: product.minimumOrderQuantity
              };
    }
};

const getAddToCartEntries =
    (bundleProducts: Array<BundleProductModel>) =>
    (preferredStoreCode: string | undefined): Array<AddToCartEntry> =>
        bundleProducts.map(({ product, quantity }) => {
            const options = getDeliveryOptions(product, preferredStoreCode);

            return {
                product: product,
                deliveryType: options.deliveryType,
                quantity: Math.max(options.quantity, quantity),
                shopId:
                    options.deliveryType === DeliveryType.ClickAndCollect
                        ? preferredStoreCode
                        : undefined
            };
        });

const isInvalidAddToCartEntry = ({ deliveryType, shopId }: AddToCartEntry) =>
    deliveryType === DeliveryType.ClickAndCollect && !isDefined(shopId);

const ProductBundle = ({ mainProduct, productBundle }: ProductBundleProps) => {
    const { t } = useGranite();

    const dispatch = useThunkDispatch();
    const cart = useSelector(getCart);

    const storeCode = useStoreCode({
        strategy: GetStoreStrategy.Stock,
        startNewSession: true
    });

    const [storePickerModalState, setStorePickerModalState] =
        useState<StorePickerModalStates>(StorePickerModalStates.Closed);
    const [addToCartLoading, setAddToCartLoading] = useState<boolean>(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const getPreferredEntriesByPreferredShopId = useCallback(
        getAddToCartEntries([
            { product: mainProduct, quantity: productBundle.sourceQuantity },
            ...productBundle.bundleProducts
        ]),
        [mainProduct, productBundle.bundleProducts]
    );

    const addEntryToCart = async (entry: AddToCartEntry) => {
        const productInCart = cart?.items.find(
            (cartItem) => cartItem.sku === entry.product.sku
        );
        if (productInCart) return;

        await dispatch(
            CartModule.addProductTileProductToCart(
                {
                    shopId: entry.shopId,
                    sku: entry.product.sku,
                    quantity: entry.quantity,
                    deliveryType: entry.deliveryType
                },
                entry.product
            )
        );
    };

    const addBundleToCart = async (preferredStoreCode: string | undefined) => {
        setAddToCartLoading(true);
        for await (const entry of getPreferredEntriesByPreferredShopId(
            preferredStoreCode
        )) {
            await addEntryToCart(entry);
        }
        setAddToCartLoading(false);
    };

    const onAddToCart = async () => {
        // Show storePickerModal if bundle has product with C&C only and no store selected
        getPreferredEntriesByPreferredShopId(storeCode).some(
            isInvalidAddToCartEntry
        )
            ? setStorePickerModalState(StorePickerModalStates.AddToCart)
            : addBundleToCart(storeCode);
    };

    const handleOnStorePickerModalSubmit = async (
        store: CloudStore,
        onClose?: () => void
    ) => {
        addBundleToCart(store.code);
        onClose?.();
    };

    const handleOnStorePickerModalClose = () =>
        setStorePickerModalState(StorePickerModalStates.Closed);

    return (
        <>
            <ProductBundleComponent
                cart={cart ?? undefined}
                mainProduct={mainProduct}
                mainProductAmount={productBundle.sourceQuantity}
                bundleProducts={productBundle.bundleProducts}
                onAddToCart={onAddToCart}
                addToCartLoading={addToCartLoading}
            />
            <StorePickerModal
                type={StorePickerType.SELECT}
                isOpen={storePickerModalState !== StorePickerModalStates.Closed}
                storeCode={storeCode ?? null}
                submitButtonText={t(
                    "general.storepicker.button.confirmStoreAndOrder"
                )}
                infoLink={null}
                fixedFeatures={[StorePickerFeature.BUY_ONLINE_PICKUP_IN_STORE]}
                displayStockLevel={false}
                onSubmit={handleOnStorePickerModalSubmit}
                onClose={handleOnStorePickerModalClose}
            />
        </>
    );
};

export { ProductBundle };
